import { Component, OnInit, HostListener, Input, Output, EventEmitter, ViewChild, ElementRef, ViewEncapsulation, TemplateRef, ChangeDetectionStrategy} from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { DropdownSearchMultipleGridLayoutService } from '@irb-common/components/dropdown-search-multiple/dropdown-search-multiple.grid-layout.service';
import { IDropdownItem } from '@irb-common/models/dropdown.model';
import { HoverPanelService } from '@irb-common/components/hover-panel/hover-panel.service';
import { UtilityService } from '@irb/irb-common/services/utility.service';


@Component({ standalone: false,
  selector: 'irb-dropdown-search-multiple',
  templateUrl: './dropdown-search-multiple.component.html',
  styleUrls: ['./dropdown-search-multiple.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class DropdownSearchMultipleComponent implements OnInit {
  @ViewChild('display') display: ElementRef;
  @ViewChild('displayContainer') displayContainer: ElementRef;
  @ViewChild('input') input: ElementRef;
  @ViewChild('panel') panel: ElementRef;
  
  private _values: Array<IDropdownItem> = null;
  private originalValues: Array<IDropdownItem> = null;
  data: Array<IDropdownItem> = []; 
  @Input() width: number = 260;
  @Input() hoverPanelWidth: number = 300;
  @Input() hoverPanelZIndex: number;
  @Input() keepChecks: boolean = true;
  @Input() enableHiddenSearchResults: boolean = false;
  @Input() enableEmptySearchTerm: boolean = false;
  @Input() showApplyButton: boolean = false;
  @Input() addNewItem: any;
  @Input() applyOnClear: boolean = false;
  @Input() cellStyle: string = 'default';
  @Input() noOptionsTemplate: TemplateRef<any>;
  @Input() canEdit: boolean = true;
  @Input() showNone: boolean = true;
  @Input() noneDisplayText: string = 'none';
  @Input() placeholder: string = '';
  @Input() hiddenLabelPanelTitle: string = 'Labels';
  @Input() hiddenLabelDescription: string = 'labels';
  @Input() showAsAll:boolean = false;
  @Output() valuesChange = new EventEmitter<IDropdownItem[]>();
  @Output() selection = new EventEmitter<IDropdownItem[]>();
  @Output() search = new EventEmitter<string>();
  @Output() applySelection = new EventEmitter<IDropdownItem[]>();
  @Output() openNewItemFlow = new EventEmitter<any>();
    
  inputControl = new FormControl();
  grid: any;
  gridOptions: any;
  searchString: string = "";
  hilitedRowIndex: number;
  lastHilitedRowIndex: number;
  displayValue: string = "";
  isAllSelected: boolean = true;
  private justTextCleared: boolean = false;
  private appliedData: Array<IDropdownItem> = null;
  //modules: Module[] = AllModules;
  showSearchResults: boolean = (this.enableHiddenSearchResults) ? false : true;
  isVisible: boolean = false;
  gridHasFocus: boolean = false;
  showAddNewButton: boolean = false;
  isUiTest = false;
  frameworkComponents: any;
  hiddenLabelCount: number;
  hiddenLabelHeight: number;
  resizeObserver: any;
  hasResizeObserver: boolean = false;

  constructor(private gls: DropdownSearchMultipleGridLayoutService, private hps: HoverPanelService, private util: UtilityService) {
  }

  ngOnInit() {
    this.gridOptions = {
      columnDefs: this.gls.getColumnDefs(),      
      suppressMovableColumns: true,
      gridAutoHeight: false,
      overlayLoadingTemplate: "<div class='loading-spinner'></div>",
      overlayNoRowsTemplate: "<span class='ag-overlay-loading-center ag-overlay-message'>No results found.</span>",
      rowSelection: "multiple",
      rowClassRules: {
        'ag-row-hilite': (params: any) => {
          const rowIndex = params.rowIndex || params.node.rowIndex;
          return rowIndex === this.hilitedRowIndex;
        }
      }
    };
    
    this.frameworkComponents = this.gls.getFrameworkComponents();

    this.inputControl.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
    ).subscribe(val => {
      if (!this.justTextCleared) {
        if (((this.values == null) ||
            (!this.valueExists(val))) && val != null) {
          this.onValueUpdate(val);
        }
      } else {
        this.justTextCleared = false;
      }
    });

    this.resizeObserver = new ResizeObserver(() => this.setDisplayValue());
    this.setDisplayValue();
  }

  @Input() set values(v: Array<IDropdownItem>) {
    this._values = (v) ? [].concat(v) : null;
    this.setDisplayValue();
    this.appliedData = (v) ? [].concat(v) : null;
    this.originalValues = (v) ? [].concat(v) : null;
  }

  get values(): Array<IDropdownItem> {
    return this._values;
  }

  onGridReady(params: any) {    
    this.grid = params.api;     
    this.hilitedRowIndex = null;

    if(this.enableEmptySearchTerm) {
      this.onValueUpdate(this.inputControl.value);
    } else {
      this.set(this.data);
    }
  }

  hide() {
    this.gridHasFocus = false;
    this.isVisible = false;
    this.hps.remove();
    if (!this.isUiTest)
    this.grid = null;
  }

  apply() {
    if(this.grid) {
      const data = this.grid.getSelectedRows().filter((x: IDropdownItem) => x.value !== -1);

      if (data.length > 0 || (data.length === 0 && this.keepChecks)) {
        this.values = data;
        this.valuesChange.emit(this.values);
        this.setDisplayValue();
        this.selection.emit(this.values);
        this.appliedData = [].concat(this.values);
      }
    }
  }

  fireApplySelection() {
    this.applySelection.emit(this.values);
  }

  clearText() {
    this.justTextCleared = true;
    this.inputControl.setValue('');
    this.onValueUpdate(this.inputControl.value);
    this.searchString = '';
  }

  clear() {
    this.values = null;
    this.valuesChange.emit(this.values);
    this.setDisplayValue();
    this.inputControl.setValue('');
    this.appliedData = null;
    this.onValueUpdate(this.inputControl.value);
    this.selection.emit(this.values);
    this.searchString = '';
    if (this.applyOnClear)
      this.applySelection.emit(this.values);
  }

  clearApplied() {
    this.appliedData = null;
  }

  onRowClicked(event: any) {    
    this.selectRow(event.node.rowIndex);

    if(!this.showApplyButton) {
      this.apply();
    }
    
    this.focusGrid();
    this.hilitedRowIndex = event.node.rowIndex;
  }

  checkSelectAllChanged(node: any) {
    if(node.data.value === -1) {
      (node.isSelected()) ? this.selectAll() : this.deselectAll();
    } else {
      this.updateSelectAll();
    }
  }

  selectAll() {
    this.isAllSelected = true;

    this.grid?.forEachNode((node: any) => {
      node.setSelected(true);
    });
  }

  deselectAll() {
    this.isAllSelected = false;

    this.grid?.forEachNode((node: any) => {
      node.setSelected(false);
    });
  }

  updateSelectAll() {
    this.isAllSelected = true;

    this.grid?.forEachNode((node: any) => {
      if(node.data.value !== -1 && !node.isSelected()) {
        this.isAllSelected = false;
      }
    });

    this.grid.getDisplayedRowAtIndex(0).setSelected(this.isAllSelected);
  }

  setDisplayValue() {
    if(this.cellStyle === "default") {
      if (this.values && this.values.length !== 0) {
        if (this.isAllSelected && this.showAsAll) {
          this.displayValue = 'All';
          return;
        }

        const names: Array<string> = [];

        this.values.forEach((v: IDropdownItem) => {
          names.push(v.name);
        });

        this.displayValue = "(" + this.values.length + ") " + names.join(", ");

        setTimeout(() => {
          if(this.util.hasTruncation(this.displayContainer.nativeElement as HTMLElement) && this.values.length > 1) {
            this.displayValue = "(" + this.values.length + " Selected)";
          }
        });
      } else {
        this.displayValue = "-Search-";
      }
    } else if(this.cellStyle === "labels") {
      if(this.values && this.values.length !== 0) {
        this.util.sortByKey(this.values, "name", "asc");
      }

      setTimeout(() => {
        this.hiddenLabelCount = 0;
        let c = this.displayContainer?.nativeElement.querySelector(".label.container");

        if(c) {
          if(c.children.length > 0 && !this.hasResizeObserver) {
            this.resizeObserver.observe(c);
            this.hasResizeObserver = true;
          } else if(c.children.length === 0 && this.hasResizeObserver) {
            this.resizeObserver.unobserve(c);
          }

          let maxWidth = c.offsetWidth;
          let aggButtonWidth = 0;
          let aggButtonHeight = 0;
          let visibleButtonCount = 0;
          let buttonTotal = c.children.length;

          for(let i = 0; i < c.children.length; i++) {
            aggButtonWidth += c.children[i].offsetWidth;
            aggButtonHeight += c.children[i].offsetHeight;

            if(i < (c.children.length - 1)) {
              aggButtonWidth += 5;
              aggButtonHeight += 5;
            }

            if(aggButtonWidth <= maxWidth) {
              visibleButtonCount++;
            }
          }

          this.hiddenLabelCount = buttonTotal - visibleButtonCount;
          aggButtonHeight = (aggButtonHeight > 220) ? 220 : aggButtonHeight;
          this.hiddenLabelHeight = 52 + aggButtonHeight;
        }
      });
    }
  }

  valueExists(s: string): boolean {
    let b: boolean = false;

    this.values.forEach((v: IDropdownItem) => {
      if(v.name === s) {
        b = true;
      }
    });

    return b;
  }

  setValue(n: Array<IDropdownItem>) {
    this.values = n;
    this.valuesChange.emit(this.values);
    this.setDisplayValue();
    this.appliedData = [].concat(n);
  }

  hasValue() {
    return this.values && this.values.length !== 0;
  }

  hasChange() {
    if(this.showApplyButton && this.grid) {
      const data = this.grid.getSelectedRows()?.filter((x: IDropdownItem) => x.value !== -1) || [];

      if(this.originalValues) {
        return !this.util.compare(this.originalValues, data, ['match'], false);
      } else {
        return data.length > 0;
      }
    } else {
      return !this.util.compare(this.originalValues, this._values, [], false);
    }
  }

  onValueUpdate(s: any) {
    this.showAddNewButton = false;

    if ((typeof s === "string" && s.length > 0) || this.enableEmptySearchTerm) {
      if(this.grid) {
        this.grid.setGridOption("rowData", []);
        this.grid.showLoadingOverlay();
      }

      this.searchString = s;
      this.search.emit(s);
    } else {
      if(this.grid) {
        this.searchString = '';
        this.search.emit('');
      }
    }
  }

  set(m: Array<IDropdownItem>) {
    m = m || [];
    if (m.length !== 0) {
      m = m[0] ? m : [];
    }
    m = m.length !==0 ? m.filter((v: IDropdownItem) => v.value !== -1) : [];
    this.data = (this.values) ? this.values.filter((v: IDropdownItem) => v.value !== -1) || [] : [];

    if(m.length > 0) {
      if(this.values) {
        this.data = this.data.concat(m.filter((x: IDropdownItem) => 
          this.values.find((v: IDropdownItem) => (x.value === v.value)) === undefined
        ));
      } else {
        this.data = m;
      }
    }

    if (this.appliedData) {
      this.data = this.combineData();
    }
    
    this.data.forEach((obj: any) => { obj['match'] = this.searchString || ''; });

    if(this.grid) {
      if (this.addNewItem && this.data.length === 0) {
        this.showAddNewButton = this.data.length === 0;
      }

      if(this.data.length > 0 && this.data[0].value !== -1) {
        this.data.unshift({name: "(Select All)", value: -1, match: ""});
      }

      if(this.searchString?.length > 0 && this.data.length === 0) {
        this.grid?.setGridOption('overlayNoRowsTemplate', "<span class='ag-overlay-loading-center ag-overlay-message'>No items match your search for '" + this.searchString + "'.<br/><span style='color: #999'>Try searching for another term.</span>");
      }

      this.grid.setGridOption("rowData", this.data);
      this.selectDefaultRows();
    }
  }

  private combineData() {
    let isExists: Array<IDropdownItem>;

    if(this.appliedData && this.appliedData.length !== 0) {
      this.util.sortByKey(this.appliedData, "name", "asc");
    }

    const combineData: Array<IDropdownItem> = [].concat(this.appliedData);

    if (this.data.length !== 0) {
      this.data.forEach((d: any) => {
        isExists = combineData.filter((v: IDropdownItem) => v.value === d.value);
        if (isExists.length === 0) {
          combineData.push(d);
        }
      });
    }
    
    return combineData;
  }

  private selectDefaultRows() {
    if(this.appliedData) {
      this.grid?.forEachNode((node: any) => {
        this.appliedData.forEach((v: IDropdownItem) => {
          if(node.data.value === v.value) {
            this.selectRow(node.rowIndex);
          }
        });
      });
    }
  }

  selectRow(index: number) {
    if(this.grid && typeof index !== "undefined") {
      if(index < 0) {
        index = 0;
      } else if(index > this.grid.getDisplayedRowCount() - 1) {
        index = this.grid.getDisplayedRowCount() - 1;
      }

      if(index >= 0) {
        this.hilitedRowIndex = index;

        this.grid?.forEachNode((node: any) => {
          if(node.rowIndex === index) {
            node.setSelected(!node.isSelected());
            this.checkSelectAllChanged(node);
          }
        });  
        
        this.grid.redrawRows();
      }
    }
  }

  hiliteRow(index: number) {
    if(this.grid && typeof index !== "undefined") {
      if(index < 0) {
        index = 0;
      } else if(index > this.grid.getDisplayedRowCount() - 1) {
        index = this.grid.getDisplayedRowCount() - 1;
      }
  
      window.setTimeout(() => {
        if(index >= 0) {    
          const updatedNodes: any = [];     
          this.hilitedRowIndex = index;

          this.grid?.forEachNode((node: any) => {
            if(node.rowIndex === index || node.rowIndex === this.lastHilitedRowIndex) {
              updatedNodes.push(node);
            }
          });

          this.grid.redrawRows({rowNodes: updatedNodes});
          this.grid.ensureIndexVisible(index);       
          this.lastHilitedRowIndex = this.hilitedRowIndex;   
        }
      },0);
    }    
  }

  focusSearch() {
    setTimeout(() => {
      if (this.input && this.input.nativeElement) {
        this.input.nativeElement.focus();
        const s = (typeof this.inputControl.value === "string") ? this.inputControl.value : "";
        //this.input.nativeElement.setSelectionRange(s.length, s.length);
        this.input.nativeElement.select();
        this.showSearchResults = (this.enableHiddenSearchResults) ? s.length > 0 : true;
        this.isVisible = this.showSearchResults;
        this.originalValues = this.util.clone(this._values);
        this.hps.resize();
      }
    });
  }

  blurSearch() {
    setTimeout(() => {
      this.input?.nativeElement.blur();
    });
  }

  setSearchResultVisibility() {
    const s = (typeof this.inputControl.value === "string") ? this.inputControl.value : "";
    this.showSearchResults = (this.enableHiddenSearchResults) ? s.length > 0 : true;
    this.isVisible = this.showSearchResults;
    this.hps.resize();
  }

  focusGrid() {
    if(this.grid && !this.gridHasFocus) {
      this.gridHasFocus = true;
      
      if(this.hilitedRowIndex === null) {
        this.hiliteRow(0);
      }      
    }
  }

  unFocusGrid() {
    this.gridHasFocus = false;
  }

  incrementRowHilite() {
    if(typeof this.hilitedRowIndex !== "undefined") {
      this.blurSearch();
      this.hiliteRow(++this.hilitedRowIndex);
    }    
  }

  decrementRowHilite() {
    if(typeof this.hilitedRowIndex !== "undefined") {
      this.blurSearch();
      this.hiliteRow(--this.hilitedRowIndex);
    }    
  }

  @HostListener('window:keydown', ['$event'])
  onKeyUp(event: any) {
    if(this.isVisible && this.display && this.gridHasFocus) {
      event.preventDefault();
      switch (event.key) {
        case "ArrowDown":
          this.incrementRowHilite();
          return true;
        case "ArrowUp":
          this.decrementRowHilite();
          return true;
        case "Enter":
          this.fireApplySelection();
          this.hide();
          return true;
        case " ":
          this.selectRow(this.hilitedRowIndex);
          this.apply();
          return true;
      }
    }
  }

  @HostListener('window:mousedown', ['$event'])
  onWindowClick(e: any) {
    if(this.isVisible && this.display) {
      if(!(this.display.nativeElement.contains(e.target))) {
        this.setDisplayValue();
      }
    }
  }
  
  onHoverPanelClose() {
    this.hide();
  }

  addNewItemClick() {
    let dataParam: any = {
      dropdownSearch: true,
      dropdownSearchValue: this.inputControl.value
    };
    this.openNewItemFlow.emit(dataParam);
    this.showAddNewButton = false;
    this.hide();
  }
}
